/**
 * This file manages paywall action discounts for marketing campaigns.
 * It defines the structure of a discount action, including the paywall headers,
 * discount percentage, coupon code, and the start and end times of the campaign.
 * The actionDiscounts array contains the details of each discount action,
 * such as the Black Friday campaign.
 *
 * The function getPaywallActionDiscounts returns the currently
 * active discount action based on the current time.
 */
import { ReactElement } from 'react';
import { PaywallMode } from '@nx-frontend/shared/types/paywall-types';

export type PaywallAction = 'blackFriday' | 'christmas';

interface PaywallActionDiscountHeader {
  title?: string | ReactElement;
  titleLocKey?: string;
  description?: string | ReactElement;
  descriptionLocKey?: string;
}

export interface PaywallActionDiscounts {
  code: PaywallAction;
  headers: Record<PaywallMode, PaywallActionDiscountHeader>;
  start: number;
  end: number;
  discount: number;
  orderSummaryGroupTitle?: string;
  orderSummaryGroupTitleLocKey?: string;
  orderSummaryItemTitle?: string;
  orderSummaryItemTitleLocKey?: string;
  coupon: string;
}

const BF_DISCOUNT = 35;
const XMAS_DISCOUNT = 40;

export const actionDiscounts: PaywallActionDiscounts[] = [
  {
    code: 'blackFriday',
    headers: {
      trialStart: {
        title: `Get your perfect plan with Black Friday sale!`,
        titleLocKey: 'features.paywall.title_get_your_plan_bf',
        description: 'Enjoy exclusive offers from 21st to 30st of November',
        descriptionLocKey: 'features.paywall.black_friday.trial_start_description',
      },
      trialEnd: {
        title: `Trial period has ended. Want to keep all features?`,
        titleLocKey: 'features.paywall.title_trial_ended_keep_features_1',
        description: 'Upgrade with Black Friday discount from 21st to 30st of November',
        descriptionLocKey: 'features.paywall.black_friday.trial_end_description',
      },
      upgrade: {
        title: `Upgrade your plan to boost client experiences`,
        titleLocKey: 'features.paywall.title_boost_client_experience',
      },
      addons: {},
    },
    orderSummaryGroupTitle: 'Black Friday discount',
    orderSummaryGroupTitleLocKey: 'features.paywall.black_friday.order_summary_group_title',
    orderSummaryItemTitle: 'Coupon savings',
    orderSummaryItemTitleLocKey: 'features.paywall.coupon_savings',
    start: 0,
    end: 1733039999000,
    discount: BF_DISCOUNT,
    coupon: 'bf2024',
  },
  {
    code: 'christmas',
    headers: {
      trialStart: {
        title: `Winter deals — ${XMAS_DISCOUNT}% Off!`,
        titleLocKey: 'features.paywall.title_winter_deals',
        description: ' For one week only, from 19st to 27st of December',
        descriptionLocKey: 'features.paywall.christmas.trial_start_description',
      },
      trialEnd: {
        title: `Trial period has ended. Keep all features?`,
        titleLocKey: 'features.paywall.title_trial_ended_keep_features_2',
        description: 'Upgrade with Winter deals discount from 19st to 27st of December',
        descriptionLocKey: 'features.paywall.christmas.trial_end_description',
      },
      upgrade: {
        title: `Upgrade your plan to boost client experiences`,
        titleLocKey: 'features.paywall.title_boost_client_experience',
      },
      addons: {},
    },
    orderSummaryGroupTitle: 'Holiday discount',
    orderSummaryGroupTitleLocKey: 'features.paywall.christmas.order_summary_group_title',
    orderSummaryItemTitle: 'Coupon savings',
    orderSummaryItemTitleLocKey: 'features.paywall.christmas.order_summary_item_title',
    start: 0,
    end: 1735459200000,
    discount: XMAS_DISCOUNT,
    coupon: 'merry2024',
  },
];

export const getPaywallActionDiscounts = (): PaywallActionDiscounts | null => {
  if (typeof window === 'object') {
    // Disable discount actions for e2e tests
    if (localStorage.getItem('paywallActionDiscountsOff') === 'true') {
      return null;
    }
  }

  const currentTime = Date.now();

  for (const action of actionDiscounts) {
    if (currentTime >= action.start && currentTime <= action.end) {
      return action;
    }
  }

  return null;
};
